import React, { useState } from 'react';
import { Grid, Typography, Button, Box } from '@mui/material';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GoogleMapsAutoComplete from './GoogleMapsAutoComplete';
import GuestPriceEstimator from './GuestPriceEstimator';
import friendlyDriverImg from '../assets/Homepage/friendly-driver.png';

const Hero = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const navigate = useNavigate();
  const role = props.role;
  const bookingPath = props.bookingPath || '/addbookings';
  const bookingState = props.bookingState;
  const heading = props.heading;
  const subheading = props.subheading;
  const heroImage = props.heroImage || friendlyDriverImg;
  const primaryCtaLabel = props.primaryCtaLabel;
  const secondaryCtaLabel = props.secondaryCtaLabel || props.ctaLabel;
  const showDriverCard = props.showDriverCard !== false;
  const driverCardTitle = props.driverCardTitle;
  const driverCardSubtitle = props.driverCardSubtitle;
  const heroTheme = props.heroTheme || {};
  const templateKey = props.templateKey || heroTheme.variant || 'classic';
  const heroBackground = heroTheme.background || '#000';
  const heroText = heroTheme.text || '#fff';
  const heroSubtext = heroTheme.subtext || 'rgba(255,255,255,0.7)';
  const primaryButtonBg = heroTheme.primaryButtonBg || '#000';
  const primaryButtonText = heroTheme.primaryButtonText || '#fff';
  const primaryButtonHover = heroTheme.primaryButtonHover || '#333';
  const secondaryButtonText = heroTheme.secondaryButtonText || '#000';
  const secondaryButtonBorder = heroTheme.secondaryButtonBorder || '#e0e0e0';
  const secondaryButtonHoverBg = heroTheme.secondaryButtonHoverBg || '#f5f5f5';
  const cardBg = heroTheme.cardBg || '#fff';
  const cardText = heroTheme.cardText || '#000';
  const isDarkHero = heroText === '#ffffff';
  const isMinimal = templateKey === 'minima' || templateKey === 'minimal';
  const isMetro = templateKey === 'metro';
  const isCore = templateKey === 'thecore' || templateKey === 'core';
  const isCommerce = templateKey === 'megamart' || templateKey === 'commerce';

  const [pickup, setPickup] = useState(null);
  const [destination, setDestination] = useState(null);
  const [showPrices, setShowPrices] = useState(false);

  const goToBooking = () => {
    const nextState = { ...(bookingState || {}) };
    if (pickup) nextState.pickup = pickup;
    if (destination) nextState.drop = destination;
    const hasState = Object.keys(nextState).length > 0;
    navigate(bookingPath, hasState ? { state: nextState } : undefined);
  };

  const handleSeePrices = () => {
    if (pickup?.coords && destination?.coords) {
      setShowPrices(true);
    } else if (role) {
      goToBooking();
    } else {
      navigate('/login', { state: { from: bookingPath } });
    }
  };

  const handleSchedule = () => {
    if (role) {
      goToBooking();
    } else {
      navigate('/login', { state: { from: bookingPath } });
    }
  };

  return (
    <Box
      sx={{
        background: heroBackground,
        minHeight: { xs: 'auto', md: '100vh' },
        display: 'flex',
        pt: { xs: 10, md: '64px' },
        position: 'relative',
        overflow: 'hidden',
      }}
      style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}
    >
      {/* Decorative overlays per template (kept subtle; never blocks clicks) */}
      {isMetro ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.22,
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)',
            backgroundSize: '56px 56px',
          }}
        />
      ) : null}
      {isCore ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background:
              'radial-gradient(900px 500px at 10% 20%, rgba(56,189,248,0.18), transparent 60%), radial-gradient(700px 500px at 85% 30%, rgba(34,197,94,0.12), transparent 60%)',
          }}
        />
      ) : null}
      {isCommerce ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.18,
            background:
              'radial-gradient(800px 520px at 20% 20%, rgba(34,197,94,0.22), transparent 60%), radial-gradient(700px 520px at 85% 30%, rgba(255,255,255,0.10), transparent 60%)',
          }}
        />
      ) : null}
      <Grid container sx={{ flex: 1 }}>
        {/* Left Content */}
        <Grid item xs={12} md={isMinimal ? 12 : 6} lg={isMinimal ? 12 : 5}>
          <Box
            sx={{
              p: { xs: 3, md: 5, lg: 6 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              height: '100%',
              maxWidth: isMinimal ? 760 : 580,
              mx: isMinimal ? 'auto' : 0,
            }}
          >
            {/* Main headline */}
            <Typography
              variant="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: 36, sm: 44, md: isMinimal ? 54 : 48 },
                lineHeight: 1.05,
                color: heroText,
                mb: 4,
                fontFamily: '"UberMove", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                letterSpacing: '-1px',
                textAlign: isMinimal ? 'center' : 'left',
              }}
            >
              {heading || t('hero_title') || 'Go anywhere with Plukk'}
            </Typography>
            {subheading ? (
              <Typography
                sx={{
                  color: heroSubtext,
                  fontSize: 18,
                  lineHeight: 1.5,
                  mb: 3,
                  maxWidth: 520,
                  mx: isMinimal ? 'auto' : 0,
                  textAlign: isMinimal ? 'center' : 'left',
                }}
              >
                {subheading}
              </Typography>
            ) : null}

            {/* Booking Card */}
            <Box
              sx={{
                borderRadius: 2,
                p: 2,
                border: isDarkHero ? '1px solid rgba(255,255,255,0.14)' : '1px solid rgba(15,23,42,0.08)',
                boxShadow: isMetro || isCore ? '0 18px 42px rgba(0,0,0,0.22)' : '0 10px 26px rgba(15,23,42,0.10)',
                backdropFilter: isMetro || isCore ? 'blur(10px)' : 'none',
                bgcolor:
                  isMetro || isCore
                    ? (isDarkHero ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.88)')
                    : cardBg,
              }}
            >
              {/* Pickup Input */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ width: 40, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                  <RadioButtonCheckedIcon sx={{ fontSize: 10, color: cardText }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <GoogleMapsAutoComplete
                    placeholder={t('enter_pickup') || 'Enter pickup location'}
                    variant="standard"
                    value={pickup}
                    onChange={(value) => {
                      setPickup(value);
                      setShowPrices(false);
                    }}
                    style={{ width: '100%' }}
                  />
                </Box>
              </Box>

              {/* Destination Input */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ width: 40, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                  <Box sx={{ width: 10, height: 10, bgcolor: cardText, borderRadius: 0.5 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <GoogleMapsAutoComplete
                    placeholder={t('enter_destination') || 'Enter destination'}
                    variant="standard"
                    value={destination}
                    onChange={(value) => {
                      setDestination(value);
                      setShowPrices(false);
                    }}
                    style={{ width: '100%' }}
                  />
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSeePrices}
                  disabled={!pickup?.coords || !destination?.coords}
                  sx={{
                    bgcolor: primaryButtonBg,
                    color: primaryButtonText,
                    py: 1.5,
                    fontSize: 15,
                    fontWeight: 500,
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: primaryButtonHover,
                    },
                    '&:disabled': {
                      bgcolor: '#ccc',
                      color: '#666',
                    },
                  }}
                >
                  {primaryCtaLabel || t('see_prices') || 'See prices'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleSchedule}
                  startIcon={<AccessTimeIcon />}
                  sx={{
                    borderColor: secondaryButtonBorder,
                    color: secondaryButtonText,
                    py: 1.5,
                    px: 3,
                    fontSize: 15,
                    fontWeight: 500,
                    borderRadius: 2,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      borderColor: secondaryButtonText,
                      bgcolor: secondaryButtonHoverBg,
                    },
                  }}
                >
                  {secondaryCtaLabel || t('schedule') || 'Schedule'}
                </Button>
              </Box>

              {/* Live Price Estimator */}
              {showPrices && (
                <GuestPriceEstimator
                  pickup={pickup}
                  destination={destination}
                  onSelect={(car) => console.log('Selected car:', car)}
                />
              )}
            </Box>

            {/* Driver CTA */}
            {showDriverCard && (
              <Box
                sx={{
                  bgcolor: isDarkHero ? 'rgba(255,255,255,0.10)' : '#f5f5f5',
                  borderRadius: 2,
                  p: 3,
                  mt: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                  '&:hover': {
                    bgcolor: isDarkHero ? 'rgba(255,255,255,0.16)' : '#e8e8e8',
                  },
                }}
                onClick={() => navigate('/login')}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 16, color: isDarkHero ? '#fff' : '#000', mb: 0.5 }}>
                    {driverCardTitle || t('earn_on_your_terms') || 'Earn on your terms'}
                  </Typography>
                  <Typography sx={{ color: isDarkHero ? 'rgba(255,255,255,0.75)' : '#6B6B6B', fontSize: 14 }}>
                    {driverCardSubtitle || t('drive_when_you_want') || 'Drive when you want, earn what you need.'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: isDarkHero ? '#fff' : '#000',
                    color: isDarkHero ? '#000' : '#fff',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 20,
                  }}
                >
                  →
                </Box>
              </Box>
            )}
          </Box>
        </Grid>

        {/* Right Image */}
        <Grid item xs={12} md={6} lg={7} sx={{ display: { xs: 'none', md: isMinimal ? 'none' : 'block' } }}>
          <Box
            sx={{
              height: '100%',
              minHeight: 600,
              position: 'relative',
            }}
          >
            <Box
              component="img"
              src={heroImage}
              alt={t('friendly_driver') || 'Friendly driver'}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center top',
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Hero;
